import React from "react";
import { motion } from "framer-motion";
import type { ContentSlideData, ContentElement } from "@/types/lesson";
import { lessonPhotoUrl } from "@/lib/api";
import { emojiFallbackForKeyword } from "@/lib/namedIcons";
import { findSlideVisual, lottieFallbackUrlsForCodepoint, notoEmojiLottieUrl } from "@/lib/lottieVisual";
import { LottiePlayer } from "@/components/LottiePlayer";
import { ElementText } from "./elements/ElementText";
import { ElementTerm } from "./elements/ElementTerm";
import { ElementExample } from "./elements/ElementExample";
import { ElementSteps } from "./elements/ElementSteps";
import { ElementTip } from "./elements/ElementTip";
import { ElementBarChart } from "./elements/ElementBarChart";
import { ElementLineChart } from "./elements/ElementLineChart";
import { ElementPieChart } from "./elements/ElementPieChart";
import { ElementComparisonTable } from "./elements/ElementComparisonTable";
import { ElementProcessFlow } from "./elements/ElementProcessFlow";
import { ElementLabeledDiagram } from "./elements/ElementLabeledDiagram";
import { ElementErrorBoundary } from "./elements/ElementErrorBoundary";
import { MathRichText } from "@/lib/mathRichText";

const BANNER_COLORS = [
  "from-pink-400 to-rose-500",
  "from-violet-400 to-purple-500",
  "from-blue-400 to-indigo-500",
  "from-cyan-400 to-blue-500",
  "from-emerald-400 to-teal-500",
  "from-amber-400 to-orange-500",
  "from-rose-400 to-pink-500",
  "from-indigo-400 to-violet-500",
];

function renderElement(el: ContentElement, i: number, isChildTheme: boolean) {
  const key = `${i}-${el.type}`;
  let inner: React.ReactNode = null;
  if (el.type === "text") inner = <ElementText data={el} isChildTheme={isChildTheme} />;
  else if (el.type === "term") inner = <ElementTerm data={el} index={i} isChildTheme={isChildTheme} />;
  else if (el.type === "example") inner = <ElementExample data={el} isChildTheme={isChildTheme} />;
  else if (el.type === "steps") inner = <ElementSteps data={el} isChildTheme={isChildTheme} />;
  else if (el.type === "tip") inner = <ElementTip data={el} isChildTheme={isChildTheme} />;
  else if (el.type === "bar_chart") inner = <ElementBarChart data={el} />;
  else if (el.type === "line_chart") inner = <ElementLineChart data={el} />;
  else if (el.type === "pie_chart") inner = <ElementPieChart data={el} />;
  else if (el.type === "comparison_table") inner = <ElementComparisonTable data={el} />;
  else if (el.type === "process_flow") inner = <ElementProcessFlow data={el} />;
  else if (el.type === "labeled_diagram") inner = <ElementLabeledDiagram data={el} />;
  if (!inner) return null;
  return (
    <ElementErrorBoundary key={key} label={el.type}>
      {inner}
    </ElementErrorBoundary>
  );
}

interface ContentSlideProps {
  slide: ContentSlideData;
  bannerIndex: number;
  isChildTheme: boolean;
}

export function ContentSlide({ slide, bannerIndex, isChildTheme }: ContentSlideProps) {
  const bannerClass = BANNER_COLORS[bannerIndex % BANNER_COLORS.length];
  const hasImage = !!slide.image?.unsplash_query?.trim();
  const visual = findSlideVisual(slide);
  const showLottie = visual.kind === "lottie";
  const hasRightColumn = hasImage || showLottie;

  const bannerInner = isChildTheme ? (
    <>
      <span className="opacity-90" aria-hidden>
        ✦{" "}
      </span>
      <MathRichText text={slide.title} childTheme />
      <span className="opacity-90" aria-hidden>
        {" "}
        ✦
      </span>
    </>
  ) : (
    <MathRichText text={slide.title} />
  );

  const bannerClassName = isChildTheme
    ? "px-4 py-3 rounded-t-lg bg-gradient-to-r from-pink-500 to-red-500 text-white font-extrabold text-lg md:text-xl italic text-center shadow-md shrink-0 tracking-tight"
    : `px-4 py-2 rounded-t-lg bg-gradient-to-r ${bannerClass} text-white font-bold text-lg shrink-0`;

  return (
    <motion.div
      className={`h-full flex flex-col min-h-0 overflow-hidden rounded-xl border ${
        isChildTheme ? "border-white/20 shadow-xl lp-child-slide" : "border-slate-200/80"
      }`}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.25 }}
    >
      <div className={bannerClassName} style={{ fontFamily: "var(--lp-font-heading)" }}>
        {bannerInner}
      </div>
      <div
        className={`flex-1 flex min-h-0 p-3 md:p-5 gap-4 ${
          hasRightColumn ? "flex-col sm:flex-row" : "flex-col"
        }`}
      >
        <div
          className={`flex-1 min-w-0 overflow-y-auto space-y-3 ${
            isChildTheme ? "lp-child-elements" : ""
          }`}
        >
          {slide.elements?.map((el, i) => renderElement(el, i, isChildTheme)) ?? null}
        </div>
        {hasImage && (
          <div className="shrink-0 w-full sm:w-[min(200px,40%)] flex flex-col items-center justify-start">
            <img
              src={lessonPhotoUrl(slide.image!.unsplash_query)}
              alt={slide.image!.alt}
              className="w-full max-w-[200px] h-auto rounded-xl border-2 border-white/40 object-cover shadow-lg"
            />
          </div>
        )}
        {showLottie && (
          <div className="shrink-0 w-full sm:w-[min(220px,42%)] flex flex-col items-center justify-center lp-lottie-panel">
            <LottiePlayer
              src={notoEmojiLottieUrl(visual.codepoint)}
              fallbackSrcs={lottieFallbackUrlsForCodepoint(visual.codepoint)}
              fallbackEmoji={emojiFallbackForKeyword(visual.keyword)}
              className="w-full max-w-[200px] aspect-square"
              aria-label={`Animation for ${visual.keyword}`}
            />
            <p className="mt-2 text-xs text-center text-white/80 font-medium capitalize">
              {visual.keyword}
            </p>
          </div>
        )}
      </div>
    </motion.div>
  );
}
